import {
  Table,
  Tag,
  Avatar,
  FloatButton,
  theme,
  message,
  Card,
  Col,
  Row,
  Space,
  Typography,
  Tooltip,
} from "antd";
import { useContext, useEffect, useMemo, useState } from "react";
import { AuthenticationContext } from "../../../../contexts/authentication-context";
import { ColumnsType } from "antd/es/table";
import {
  AppstoreAddOutlined,
  ClockCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { CompanyContext } from "../../../../contexts/company-context";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import AddScheduleForm from "../../modal/addSchedule";
import Legend from "../../../legend/legend";
import { EmployeeContext } from "../../../../contexts/employee-context";
import { Day, ScheduleType } from "./week-types";
import { useSchedule } from "../../hooks/useSchedule";
import React from "react";
import CloneScheduleModal from "../../modal/CloneScheduleModal";
import WeekViewToolbar from "./weekViewToolbar";
import { downloadCSV, generateScheduleCSV } from "../../../../Util/csvExport";

const ScheduleWeek = (): JSX.Element => {
  // User and Company Context.
  const { selectedCompany } = useContext(CompanyContext);
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const { useToken } = theme;
  const { token } = useToken();
  const { Text } = Typography;

  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

  const [OpenAddSchedule, setOpenAddSchedule] = useState<boolean>(false);
  const [isManager, setIsManager] = useState<boolean>(false);
  const { employeeData } = useContext(EmployeeContext);

  // Selected Week Schedule Constants.
  const [selectedWeek, setSelectedWeek] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<React.Key>();

  const onCreate = (values: any) => {
    // Check if the values.scheduleDate is with in selected Week...
    // if it is, then let's reset the selected week only to refresh the data.
    const isMatch = selectedWeek.includes(
      dayjs(values.scheduleDate).format("YYYY-MM-DD"),
    );
    if (isMatch) {
      setSelectedWeek([...selectedWeek]);
    }

    setOpenAddSchedule(false);

    //Show Alert successfully inserted schedule???
    message.success("Successfully processed your request.");
  };

  const currDate = new Date();

  const highlightStyle = {
    backgroundColor: token.colorPrimaryBg,
  };

  const navigate = useNavigate();

  // Get the Current Week Dates based on any Date
  // This is to set the Table view Dynamically.
  // Make sure to add the offset of the
  const handleWeekChange = (selWeek: string) => {
    const current = new Date(selWeek);
    // So that this doesn't mess with the SetDate.
    current.setHours(0, 0, 0, 0);
    const week = [];
    for (let i = 0; i < 7; i++) {
      const first = current.getDate() - current.getDay() + i;
      const day = new Date(current.setDate(first)).toISOString().slice(0, 10);
      week.push(day);
    }
    setSelectedWeek(week);
    // Process UseEffect call to get new Schedule... How to best do that?
  };

  useEffect(() => {
    handleWeekChange(currDate.toISOString());
  }, []);

  const handleBulkAddClick = () => {
    navigate(`/schedule/bulkadd`);
  };

  // IS Currently Logged In user Manager or not.
  useMemo(() => {
    setIsManager(employeeData?.hasDirectReport ?? false);
  }, [userInfo, selectedCompany, employeeData]);

  // Fetch Schedule Data.
  const {
    selectedWeekSchedule,
    location,
    loading: dataLoading,
  } = useSchedule(selectedWeek, selectedLocation);

  // Utility function to format the date
  const formatColumnDate = (date: string | Date): string => {
    return new Date(date).toLocaleDateString("en-us", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Utility function to render tags for a specific day
  /*const renderTags = (tags: Day[]) => {
    return tags.map((tag: any) => (
      <React.Fragment key={tag.key}>
        <Link to={{ pathname: `/schedule/${tag.key}` }}>
          <Tag color={tag.color}>{tag.time}</Tag>
        </Link>
        <br />
        <br />
      </React.Fragment>
    ));
  };*/

  const hexToRGBA = (hex: string, alpha: number) => {
    let r = 0,
      g = 0,
      b = 0;

    // Remove #
    hex = hex.replace("#", "");

    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const renderTags = (tags: Day[]) => {
    return tags.map((event: any) => (
      <Link
        to={{ pathname: `/schedule/${event.key}` }}
        style={{ textDecoration: "none" }}
      >
        <Card
          key={event.key}
          size="small"
          hoverable
          style={{
            width: 150,
            marginBottom: 8,
            backgroundColor: event.color
              ? hexToRGBA(event.color, 0.25)
              : "#1890ff", // Light gray or customize as needed
            opacity: event.isPublished === false ? 0.8 : 1,
            boxShadow:
              "0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)",
            transition: "box-shadow 0.3s ease",
            cursor: "pointer",
          }}
        >
          <Row justify="space-between" align="top" gutter={[16, 8]}>
            <Col flex="auto">
              <Space
                direction="vertical"
                align="start"
                size={8}
                style={{ width: "100%" }}
              >
                <Space wrap align="start">
                  <Text strong style={{ color: "inherit" }}>
                    {event.Title}
                  </Text>
                  <Tag color={event.secondcolor}>{event.tag}</Tag>
                  {event.isPublished === false && (
                    <Tag color="default">Draft</Tag>
                  )}
                </Space>
                {/* <Text type="secondary">Description</Text> */}
              </Space>
            </Col>
            <Col>
              <Space align="start">
                <ClockCircleOutlined
                  style={{ color: "#8c8c8c", marginTop: 2 }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.2,
                  }}
                >
                  {event.time.split("-").map((part: any, index: any) => (
                    <Text key={index} type="secondary">
                      {part.trim()}
                    </Text>
                  ))}
                </div>
              </Space>
            </Col>
          </Row>
        </Card>
      </Link>
    ));
  };

  const isToday = (input: Date | string) => {
    return dayjs(input).isSame(dayjs(), "day");
  };

  // Helper function to create columns for each day

  // Simple hover CSS — include this globally or in your CSS module
  const styles = `
.day-cell {
  position: relative;
  min-height: 40px;
}

.day-cell .add-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
  font-size: 16px;
}

.day-cell:hover .add-icon {
  opacity: 1;
}
`;

  // Inject CSS into page once (optional)
  if (
    typeof window !== "undefined" &&
    !document.getElementById("hover-style")
  ) {
    const style = document.createElement("style");
    style.id = "hover-style";
    style.innerHTML = styles;
    document.head.appendChild(style);
  }

  const createDayColumn = (
    dayIndex: number,
    dayLabel: string,
  ): ColumnsType<ScheduleType>[0] => {
    const columnDate = selectedWeek[dayIndex];
    const isTodayColumn = isToday(columnDate);

    return {
      title: formatColumnDate(columnDate),
      dataIndex: dayLabel,
      align: "center",
      width: "13%",
      onHeaderCell: () => ({
        style: isTodayColumn ? highlightStyle : {},
      }),
      onCell: () => ({
        style: isTodayColumn ? highlightStyle : {},
      }),
      render: (_, record) => {
        const items = record.days[dayIndex];
        const isEmpty = !items || items.length === 0;

        return (
          <div className="day-cell">
            {!isEmpty ? (
              renderTags(items)
            ) : (
              <Tooltip title="Add">
                <PlusOutlined
                  className="add-icon"
                  onClick={() =>
                    console.log("Add clicked for", record, "on day", dayIndex)
                  }
                />
              </Tooltip>
            )}
          </div>
        );
      },
    };
  };

  // Columns definition
  const columns: ColumnsType<ScheduleType> = [
    {
      title: "",
      dataIndex: "name",
      width: "12%",
      render: (text: string, record: ScheduleType) => {
        return (
          <div>
            <Avatar
              style={{ backgroundColor: "#f56a00", verticalAlign: "middle" }}
              size="default"
              gap={4}
            >
              {record.initial}
            </Avatar>
            &nbsp;{text}
          </div>
        );
      },
    },
    createDayColumn(0, "sunday"),
    createDayColumn(1, "monday"),
    createDayColumn(2, "tuesday"),
    createDayColumn(3, "wednesday"),
    createDayColumn(4, "thursday"),
    createDayColumn(5, "friday"),
    createDayColumn(6, "saturday"),
  ];

  function handleLocationSearch(value: React.Key): void {
    setSelectedLocation(value);
  }

  const handleCloneSchedule = (targetDate: Date) => {
    //cloneSchedule(targetDate);
    message.success("Schedule cloned successfully!");
  };

  const handleExportSchedule = () => {
    const csvContent = generateScheduleCSV(selectedWeekSchedule, selectedWeek);
    const filename = `schedule-${dayjs(selectedWeek[0]).format(
      "YYYY-MM-DD",
    )}.csv`;
    downloadCSV(csvContent, filename);
  };

  return (
    <div>
      <WeekViewToolbar
        location={location}
        handleLocationSearch={handleLocationSearch}
        handleLocationSearchClear={() => setSelectedLocation(undefined)}
        onCurrentWeek={() => handleWeekChange(currDate.toISOString())}
        onExportSchedule={handleExportSchedule}
        onCloneSchedule={() => setIsCloneModalOpen(true)}
        onWeekChange={handleWeekChange}
      />

      <Legend />

      <Table
        style={{ whiteSpace: "nowrap" }} // Set to Pre if you don't like the Horizontal Scroll...
        columns={columns}
        dataSource={selectedWeekSchedule}
        bordered
        loading={dataLoading}
        scroll={{ x: "max-content" }} // ✅ enables horizontal scroll
      />
      {isManager ? (
        <>
          <FloatButton.Group
            trigger="hover"
            type="primary"
            style={{ right: 24 }}
            icon={<PlusCircleOutlined />}
          >
            <FloatButton
              icon={<PlusCircleOutlined />}
              onClick={() => {
                setOpenAddSchedule(true);
              }}
            />
            <FloatButton
              icon={<AppstoreAddOutlined />}
              onClick={handleBulkAddClick}
            />
          </FloatButton.Group>
          {OpenAddSchedule && (
            <AddScheduleForm
              open={OpenAddSchedule}
              onCreate={onCreate}
              onCancel={() => {
                setOpenAddSchedule(false);
              }}
            />
          )}
        </>
      ) : null}

      <CloneScheduleModal
        open={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onClone={handleCloneSchedule}
        sourceWeek={dayjs(selectedWeek[0]).toDate()}
        corpId={selectedCompany!.corporationId}
      />
    </div>
  );
};

export default ScheduleWeek;
